import { useCallback } from 'react'
import { useCookies } from 'react-cookie'
import useSWR from 'swr'
import { STORAGE_PREFIX } from '../../config'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import { UseSalesReportExport, useSalesReportExport } from '../sales-report'
import { AccessReportFilterOptions, AccessReportRequest, AccessReportResponse } from './types'

export * from './types'
export { exportErrorMessage } from '../sales-report'

export const ACCESS_REPORT_LIVE_REFRESH_MS = 2 * 60_000

const useToken = (): boolean => {
  const tokenKey = STORAGE_PREFIX + 'userJWT'
  const [cookies] = useCookies([tokenKey])
  return LOCAL_EXECUTION || Boolean(cookies[tokenKey])
}

interface UseAccessReportFilters {
  filters: AccessReportFilterOptions | undefined
  isLoading: boolean
  error: unknown
  retry: () => void
}

export const useAccessReportFilters = (eventId: number): UseAccessReportFilters => {
  const url = `producer/analytics/accessReportFilters?eventId=${eventId}`
  const hasToken = useToken()
  const ready = hasToken && Number.isSafeInteger(eventId) && eventId > 0

  const fetcher = useCallback(async (): Promise<AccessReportFilterOptions> => {
    const response = await APIDecoratorWithBaseURI().get(url)
    return response.data.body as AccessReportFilterOptions
  }, [url])

  const { data, error, isLoading, mutate } = useSWR(ready ? url : null, fetcher)

  return {
    filters: data,
    isLoading: isLoading || !hasToken,
    error,
    retry: () => {
      void mutate()
    },
  }
}

interface UseAccessReport {
  report: AccessReportResponse | undefined
  isLoading: boolean
  isValidating: boolean
  error: unknown
  refresh: () => void
}

export const useAccessReport = (eventId: number, request: AccessReportRequest | null, live: boolean): UseAccessReport => {
  const url = `producer/analytics/accessReport?eventId=${eventId}`
  const hasToken = useToken()
  const ready = hasToken && Number.isSafeInteger(eventId) && eventId > 0 && request !== null
  const body = request === null ? null : JSON.stringify(request)

  const fetcher = useCallback(async ([target, payload]: [string, string]): Promise<AccessReportResponse> => {
    const response = await APIDecoratorWithBaseURI().post(target, JSON.parse(payload))
    return response.data.body as AccessReportResponse
  }, [])

  const { data, error, isLoading, isValidating, mutate } = useSWR(ready ? [url, body] : null, fetcher, {
    keepPreviousData: true,
    revalidateOnFocus: false,
    refreshInterval: live ? ACCESS_REPORT_LIVE_REFRESH_MS : 0,
  })

  return {
    report: data,
    isLoading: isLoading || !hasToken,
    isValidating,
    error,
    refresh: () => {
      void mutate()
    },
  }
}

export const useAccessReportExport = (eventId: number): UseSalesReportExport<AccessReportRequest> =>
  useSalesReportExport<AccessReportRequest>(eventId, 'accessReportExport')
